class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);


// GARDENIA CART OPTION EDITING
const gardeniaProductCache = new Map();

async function gardeniaFetchProduct(handle) {
  if (gardeniaProductCache.has(handle)) return gardeniaProductCache.get(handle);

  const request = fetch(window.Shopify.routes.root + 'products/' + encodeURIComponent(handle) + '.js', {
    headers: { Accept: 'application/json' },
  }).then(async (response) => {
    if (!response.ok) throw new Error('Product request failed');
    return response.json();
  });

  gardeniaProductCache.set(handle, request);
  return request;
}

function gardeniaVariantOptions(variant) {
  if (Array.isArray(variant.options)) return variant.options.map(String);

  return [variant.option1, variant.option2, variant.option3]
    .filter((value) => value !== null && value !== undefined)
    .map(String);
}

async function gardeniaCartRequest(path, payload) {
  const response = await fetch(window.Shopify.routes.root + path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data.description || data.message || 'Cart request failed');
    error.data = data;
    throw error;
  }

  return data;
}

function gardeniaShowOptionError(details, message) {
  const error = details.querySelector('.gardenia-cart-option-error');
  if (!error) return;
  error.textContent = message;
  error.hidden = false;
}

function gardeniaHideOptionError(details) {
  const error = details.querySelector('.gardenia-cart-option-error');
  if (!error) return;
  error.textContent = '';
  error.hidden = true;
}

async function gardeniaChangeCartVariant(select) {
  const details = select.closest('.gardenia-gift-details');
  if (!details || details.classList.contains('is-updating')) return;

  const originalOptions = JSON.parse(details.dataset.currentOptions || '[]');
  const selectedOptions = [...originalOptions];

  details.querySelectorAll('.gardenia-cart-option-select').forEach((control) => {
    const position = Number(control.dataset.optionPosition);
    selectedOptions[position] = control.value;
  });

  details.classList.add('is-updating');
  gardeniaHideOptionError(details);

  const quantity = Number(details.dataset.quantity || 1);
  const rawProperties = JSON.parse(details.dataset.properties || '{}');
  const properties = Array.isArray(rawProperties)
    ? rawProperties.reduce((result, property) => {
        if (Array.isArray(property) && property.length >= 2) {
          result[String(property[0])] = property[1];
        } else if (property && typeof property === 'object' && property.name !== undefined) {
          result[String(property.name)] = property.value;
        }
        return result;
      }, {})
    : rawProperties || {};
  const originalVariantId = Number(details.dataset.currentVariant);
  const originalLineIndex = Number(details.dataset.lineIndex);

  try {
    const product = await gardeniaFetchProduct(details.dataset.productHandle);
    const variant = product.variants.find((candidate) => {
      const options = gardeniaVariantOptions(candidate);
      return (
        options.length === selectedOptions.length &&
        options.every((value, index) => value === String(selectedOptions[index]))
      );
    });

    if (!variant || variant.available === false) {
      throw new Error(details.dataset.unavailableMessage || 'Unavailable');
    }

    if (String(variant.id) === String(originalVariantId)) {
      details.dataset.currentOptions = JSON.stringify(selectedOptions);
      return;
    }

    const addItem = {
      id: variant.id,
      quantity,
      properties,
    };

    if (details.dataset.sellingPlan) {
      addItem.selling_plan = Number(details.dataset.sellingPlan);
    }

    if (!Number.isInteger(originalLineIndex) || originalLineIndex < 1) {
      throw new Error(details.dataset.errorMessage || 'Cart line could not be identified');
    }

    await gardeniaCartRequest('cart/change.js', {
      line: originalLineIndex,
      quantity: 0,
    });

    try {
      const addedCart = await gardeniaCartRequest('cart/add.js', {
        items: [addItem],
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname + window.location.search,
      });

      const drawer = document.querySelector('cart-drawer');

      if (drawer && addedCart.sections) {
        drawer.classList.remove('active');
        drawer.renderContents({
          ...addedCart,
          id: variant.id,
        });
      } else {
        window.location.reload();
      }
    } catch (addError) {
      const rollbackItem = {
        id: originalVariantId,
        quantity,
        properties,
      };

      if (details.dataset.sellingPlan) {
        rollbackItem.selling_plan = Number(details.dataset.sellingPlan);
      }

      await gardeniaCartRequest('cart/add.js', {
        items: [rollbackItem],
      }).catch(() => {});

      throw addError;
    }
  } catch (error) {
    details.querySelectorAll('.gardenia-cart-option-select').forEach((control) => {
      const position = Number(control.dataset.optionPosition);
      if (originalOptions[position] !== undefined) {
        control.value = String(originalOptions[position]);
      }
    });

    const message =
      error.message === details.dataset.unavailableMessage
        ? details.dataset.unavailableMessage
        : error.message || details.dataset.errorMessage;

    gardeniaShowOptionError(details, message);
  } finally {
    details.classList.remove('is-updating');
  }
}

document.addEventListener('change', (event) => {
  const select = event.target.closest('.gardenia-cart-option-select');
  if (!select) return;
  gardeniaChangeCartVariant(select);
});
